import util from "@/components/shared/util";
import { navigationNamespace } from "@/store/modules/navigation";
import { patientNamespace } from "@/store/modules/patient";
import { towerNamespace } from "@/store/modules/tower";
import { TowerAction } from "@/store/modules/tower/actions";
import { getNames } from "@/store/modules/tower/getters";
import { TOWER_ALERT_REVIEW_UPDATE } from "@/store/modules/tower/mutation_types";
import Vue from "vue";
import Component from "vue-class-component";
import { Emit, Prop } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import { TOWER_IGNORE } from "../tower";
import ChoiceList from "./ChoiceList";

const TOWER_POTENTIALLY_SIGNIFICANT = "assess.6";
const TOWER_NOT_SIGNIFICANT = "assess.7";
const TOWER_NO_COMM = "noComm.1";
const TOWER_NO_REACTION = "none.99";

@Component({
  components: { ChoiceList }
})
export default class AlertAssessmentOrAction extends Vue {
  @Action(TOWER_ALERT_REVIEW_UPDATE, towerNamespace)
  TOWER_ALERT_REVIEW_UPDATE!: Function;

  @Getter("towerChoicesIndexed", patientNamespace)
  towerChoicesIndexed!: Record<string, string>;

  @Getter("towerAlertPick", patientNamespace)
  towerAlertPick!: Record<string, string>;

  @Getter("inWrapper", navigationNamespace)
  inWrapper!: boolean;

  @Prop(Object)
  item!: any;

  @Prop(Boolean)
  disableFormElement!: boolean;

  @Prop(Boolean)
  treatmentPatient!: boolean;

  @Prop({
    default: () => {
      return "";
    }
  })
  customIssue!: string;

  entry = TowerAction.ENTRY;

  editableItem = {
    assessment: "",
    caseAction: "",
    contact: "",
    reaction: "",
    comment: ""
  };

  selectedForUpdate: boolean | string = false;
  errorMessage = "";

  selectForUpdate() {
    this.editableItem.comment = this.item.comment;
    this.selectedForUpdate = this.item.id;
  }

  @Emit()
  disableForm(isDisabled: boolean) {
    return isDisabled;
  }

  @Emit()
  finishSave() {}

  mounted() {
    if (this.newIssue && !this.item.sequence) {
      // Handle new issue case
      this.editableItem.comment = this.item.comment;
      this.selectedForUpdate = this.item.id;
    }
  }

  get showEditIssueButtons() {
    return (
      this.item.incrementSequence ||
      (!this.item.sequence && !(this.selectedForUpdate === this.item.id))
    );
  }

  get roomCalled() {
    return this.item.id === "newIssue.1";
  }

  get newIssue() {
    return this.item.atom === "newIssue";
  }

  get didContact() {
    return this.item.contact !== "";
  }

  async disable() {
    this.editableItem.assessment = TOWER_IGNORE;
    this.editableItem.caseAction = "";
    this.editableItem.contact = "";
    this.editableItem.reaction = "";
    this.editableItem.comment = "";
    await this.updateItem();
  }

  async snooze() {
    this.editableItem.assessment = TOWER_POTENTIALLY_SIGNIFICANT;
    this.editableItem.caseAction = TOWER_NO_COMM;
    this.editableItem.reaction = this.treatmentPatient ? TOWER_NO_REACTION : "";

    await this.updateItem();
  }

  async notAProblem() {
    this.editableItem.assessment = TOWER_NOT_SIGNIFICANT;
    this.editableItem.caseAction = TOWER_NO_COMM;
    this.editableItem.reaction = this.treatmentPatient ? TOWER_NO_REACTION : "";

    await this.updateItem();
  }

  async updateItem() {
    if (this.commentRequired && this.editableItem.comment.trim() === "") {
      this.errorMessage = "Comment is required";
      return;
    } else {
      this.errorMessage = "";
    }

    const item = Object.assign({}, this.item);
    Object.entries(this.editableItem).forEach(([key, value]) => {
      // If edited item has new value, overwrite it now
      // If item doesn't have a key from editableItem, add it
      if (value || !item[key]) {
        item[key] = value;
      }
    });

    // Issue entered by tower user passed in from parent component
    // because that is where the text entry is
    if (this.customIssue) {
      item.message = this.customIssue;
    }
    this.selectedForUpdate = false;
    await this.saveItem(item);
  }

  cancelUpdate() {
    this.selectedForUpdate = false;
    if (this.newIssue) {
      this.finishSave();
    }
  }

  get canSave() {
    return Object.values(this.editableItem).some(value => value !== "");
  }

  saving = false;
  private async saveItem(item: Record<string, any>) {
    this.disableForm(true);
    this.saving = true;
    await this.TOWER_ALERT_REVIEW_UPDATE({ item });
    this.saving = false;
    this.finishSave();
    if (!this.inWrapper) {
      util.refreshDataOnMainWindow();
    }
  }

  beforeDestroy() {
    // Save caused component to be destroyed because a newer version
    // has been loaded from the server
    if (this.saving) {
      this.finishSave();
    }
  }

  commentRequiredIndex: Record<string, boolean> = {};

  updateCommentRequired(key: string, commentRequired: boolean) {
    Vue.set(this.commentRequiredIndex, key, commentRequired);
  }

  get commentRequired() {
    // If at least one is required, then the comment is required
    return Object.values(this.commentRequiredIndex).some(value => value);
  }

  get summaries(): Array<{ key: string; value: string }> {
    const keyValues: Array<{ key: string; value: string }> = [];

    const comment = {
      key: "Comment",
      value: this.item.comment
    };

    if (this.item.id === "bigSave") {
      // Just the comment for Big Save
      return [comment];
    }

    keyValues.push({
      key: "Assessment",
      value: getNames(this.item.assessment, this.towerChoicesIndexed)
    });

    keyValues.push({
      key: "Action",
      value: getNames(this.item.action, this.towerChoicesIndexed)
    });

    keyValues.push({
      key: "Reaction",
      value: getNames(this.item.reaction, this.towerChoicesIndexed)
    });

    keyValues.push(comment);

    if (!this.treatmentPatient || this.item.id === "newIssue.1") {
      keyValues.push({
        key: "Contact",
        value: getNames(this.item.contact, this.towerChoicesIndexed)
      });
    }

    return keyValues.filter(summary => summary.value);
  }
}
